// Project container page — commonsku-style layout per `docs/commonsku-audit/`
// §4 (validated 2026-04-16, Session 14). Reshaped 2026-05-20: Collaborate
// sub-tab removed (operator: "We don't use this"); Tracking removed 2026-07-29
// (folded into Overview) — 10 tabs remain.
//
// Structure:
//   - Breadcrumb header (← Clients / {client.name} / Project #N — {project.name})
//   - Left sub-nav: 10 tabs (Overview / Files / Presentation / Estimate /
//     Quote / POs / Invoice / Bills / Commissions / Feedback). The
//     standalone Tracking (route `shipping`) tab was removed 2026-07-29 — its
//     aggregate carrier-tracking view folded into Overview (CEO). Greyed for
//     tabs without content yet — same lazy-instantiation pattern commonsku uses.
//   - Main column: tab content via <Outlet />
//
// Each tab is a separate route (`/projects/:id/{tab}`) so browser back/forward
// works naturally and tab state is shareable / bookmarkable.
//
// Header action wiring: the standalone Actions ▾ dropdown was removed
// 2026-07-27 (it duplicated each tab's in-context action row — CEO note). Its
// create actions each already have an in-context home — the Presentation/Estimate
// tabs' New buttons AND (added this pass) the same New affordance on each
// document's detail page, so a rep isn't trapped when the tab auto-opens a single
// doc. Its one capability without another home, Reopen, moved to a compact
// affordance beside the terminal Closed/Lost pill (below). The old
// `project-action` event bus the dropdown fired on is gone — Reopen is a direct
// mutation now that it lives in this page.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Folder,
  Presentation as PresentationIcon,
  Calculator,
  ClipboardList,
  ShoppingCart,
  Receipt,
  FileSpreadsheet,
  Coins,
  MessageCircleQuestion,
  RotateCcw,
  Truck,
  Pencil,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { StatusPill } from "@/components/ui/StatusPill";
import { OpportunitySalesBar, type OpportunitySales } from "@/components/opportunity/OpportunitySales";
import {
  projectStageLabel,
  projectStateLabel,
  isMeaningfulProjectState,
  type ProjectStageNode,
  type ProjectStageKey,
} from "@/lib/projectStage";
// deriveReopenStage kept standalone for qa-pr-c's reopen lint (sole-import regex).
import { deriveReopenStage } from "@merchd/shared";
import {
  buildProjectDeleteBody,
  projectSentDocsRefusal,
  PROJECT_HAS_SENT_DOCUMENTS,
  PROJECT_SENT_DOCS_OVERRIDE_NOTE,
  type ProjectSentDocCounts,
} from "@merchd/shared";
import { cn } from "@/lib/utils";

interface ProjectDetailResponse {
  project: {
    id: string;
    projectNumber: number;
    clientId: string;
    name: string;
    primaryContactId: string | null;
    budgetAmount: string | null;
    budgetCurrency: string | null;
    inHandsDate: string;
    clientRepId: string;
    orderRepId: string | null;
    eventType: string | null;
    statusStage: string;
    statusState: string;
    notes: string | null;
    propertyName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
  client: {
    id: string;
    name: string;
    status: string | null;
    // Read-only: the joined account has been soft-deleted (projects survive it).
    deleted: boolean;
  };
  primaryContact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    title: string | null;
  } | null;
  clientRep: { id: string; fullName: string; email: string } | null;
  /** Opportunity sales stage (CEO 2026-10-15) — null before the DB step. */
  sales?: OpportunitySales | null;
  orderRep: { id: string; fullName: string; email: string } | null;
  counts: {
    presentations: number;
    orders: number;
    estimates: number;
    salesOrders: number;
    /** After-the-sale tab badges (Stephanie 2026-10-13). */
    pos?: number;
    invoices?: number;
    bills?: number;
  };
  // Server-derived stage progression. The visual stage rail that once rendered
  // this was removed 2026-07-26 (duplicated the sub-nav below); the field stays
  // because the tabs read node counts (data.stages.find(…)?.count) for their
  // empty-state dead-end affordances — cancellation-excluding truth counts.*
  // doesn't carry. Cheap to keep, avoids API churn.
  stages: ProjectStageNode[];
  // Documents carrying a live public link — feeds the delete dialog's honest
  // pre-warning (projectSentDocsRefusal). The server DELETE stays authoritative.
  sentDocs: ProjectSentDocCounts;
  recentActivity: Array<{
    id: string;
    eventType: string;
    actorUserId: string | null;
    actorName: string | null;
    occurredAt: string;
    payload: unknown;
  }>;
}

type IconType = typeof Folder;

interface SubTab {
  to: string;
  label: string;
  icon: IconType;
  count?: number;
  ready: boolean;
  /** Side-nav group heading rendered before this tab (desktop only). */
  group?: string;
}

// Exported so the shared create mutations (useCreatePresentation) can invalidate
// the project-detail payload — the query that feeds the left sub-nav counts + the
// tab empty-state affordances — after a create, keeping them honest.
export const tabsKey = "merchd.projectDetail";

export function ProjectDetailLayout() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [tabsKey, id, "detail"],
    queryFn: () => api<ProjectDetailResponse>(`/projects/${id}/detail`),
    enabled: id.length > 0,
  });

  const queryClient = useQueryClient();
  const [reopenNotice, setReopenNotice] = useState<string | null>(null);

  // Reopen — relocated 2026-07-27 from the removed project-header Actions ▾
  // dropdown. Reopen was the dropdown's only capability without another
  // in-context home, so it moves to a compact affordance beside the terminal
  // Closed/Lost pill (below). It PATCHes the EXISTING /projects/:id endpoint
  // (status_stage) — the same stage-move mechanism the Sales Pipeline board uses
  // to mark deals lost/won. No new endpoint.
  //
  // Destination (P3): NOT a blind reset to "opportunity" — a fully-invoiced
  // closed deal reset to the top of the funnel invites doc creation that
  // collides with one-Sales-Order-per-project downstream. The shared
  // deriveReopenStage() returns the most-advanced existing document (capped at
  // sales_order), computed from the doc counts already in this payload — so no
  // extra fetch, and the destination can't drift from the gate.
  //
  // onSuccess invalidates BOTH the project-detail payload (header pill + sub-nav
  // counts) AND the Sales Pipeline board (["sales","pipeline"]) — the board's
  // 30s staleTime would otherwise keep showing the reopened deal as lost/absent.
  const reopenProject = useMutation({
    mutationFn: (targetStage: string) =>
      api(`/projects/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status_stage: targetStage }),
      }),
    onSuccess: (_res, targetStage) => {
      void queryClient.invalidateQueries({ queryKey: [tabsKey, id, "detail"] });
      void queryClient.invalidateQueries({ queryKey: ["sales", "pipeline"] });
      setReopenNotice(
        `Project reopened — back in the pipeline at ${projectStageLabel(
          targetStage,
        )}.`,
      );
    },
  });

  // Reopen-availability is derived from the payload's terminal stage
  // (closed/lost), the same condition the getAvailableActions rule engine uses —
  // no second /available-actions fetch now that the dropdown (that endpoint's
  // only client) is gone.
  useEffect(() => {
    setReopenNotice(null);
  }, [id]);

  if (isLoading) {
    return <div className="mt-4 h-24 animate-pulse rounded-md bg-white" aria-label="Loading project" />;
  }
  if (isError || !data) {
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className="mx-auto max-w-3xl py-20 text-center">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          {is404 ? "Opportunity not found" : "Couldn't load opportunity"}
        </h1>
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/projects">
            <ArrowLeft className="h-4 w-4" /> Back to projects
          </Link>
        </Button>
      </div>
    );
  }

  const { project, client, counts } = data;

  // Derive the reopen destination from the stage-node doc counts already in the
  // payload (cancellation-excluding truth counts — the same source the tab
  // empty-states use). Consumed only by the terminal Reopen affordance below.
  const stageCount = (key: ProjectStageKey): number =>
    data.stages.find((s) => s.key === key)?.count ?? 0;
  const reopenTargetStage = deriveReopenStage({
    presentations: stageCount("presentation"),
    estimates: stageCount("estimate"),
    salesOrders: stageCount("sales_order"),
  });

  const tabs: SubTab[] = [
    { to: "overview", label: "Overview", icon: FileText, ready: true, group: "Opportunity" },
    // Files tab: gallery + presigned R2 upload/download, wired 2026-05-19.
    { to: "files", label: "Files", icon: Folder, ready: true },
    {
      to: "presentation",
      group: "Selling",
      label: "Presentation",
      icon: PresentationIcon,
      count: counts.presentations,
      ready: counts.presentations > 0,
    },
    // One Quotes tab (CEO 2026-10-15): Estimate + Sales Order merged. Count =
    // every quote on the opportunity, any stage.
    {
      to: "quotes",
      label: "Quotes",
      icon: ClipboardList,
      count: counts.estimates + counts.salesOrders,
      ready: true,
    },
    // The quote's item-by-item Shipping plan (Stephanie 2026-10-12, like
    // commonsku's Shipping tab). Moved off the quote page.
    { to: "shipping", label: "Shipping", icon: Truck, ready: counts.salesOrders > 0 },
    // Production / Finance sub-tabs wired to live data via
    // `/projects/:id/<resource>` (read-only). Always navigable —
    // the tab renders an empty state when the underlying list is empty.
    // The standalone Tracking tab was removed 2026-07-29 (CEO: "tracking …
    // should be in the overview section … its own tracking section is
    // redundant"). Its aggregate carrier-tracking view now lives as a card on
    // Overview; the `/projects/:id/shipping` route survives as a redirect to
    // Overview so old deep links + browser history never 404 (route-safety,
    // the round-22 hard-refresh bug class). Tracking ENTRY stays on each PO.
    // Counts like Presentation / Quote (Stephanie 2026-10-13). Optional so an
    // older API response without them just shows no badge.
    { to: "pos", label: "POs", icon: ShoppingCart, ready: true, group: "After the sale", count: counts.pos },
    { to: "invoice", label: "Invoice", icon: Receipt, ready: true, count: counts.invoices },
    { to: "bills", label: "Bills", icon: FileSpreadsheet, ready: true, count: counts.bills },
    { to: "commissions", label: "Commissions", icon: Coins, ready: true },
    { to: "feedback", label: "Feedback", icon: MessageCircleQuestion, ready: true },
  ];

  return (
    <div className="mx-auto w-full">
      {/* Project header (handoff W08 "Record workspace"): context line (client),
          the project name at 32px, outcome/state pills, then a facts row. The
          PROJECT stage is a fact here; each document's own status lives on that
          document — the two are never merged into one pill. */}
      <div className="mb-6 mt-2 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          <Link to="/projects" className="hover:text-ink">
            Opportunities
          </Link>
          <span aria-hidden>/</span>
          {client.deleted ? (
            // DEFENSIVE BACKSTOP, not an expected state: since CEO 2026-07-29 an
            // account with active projects CAN'T be deleted, so a live project
            // should never see a deleted account. This handles only historical
            // orphans (accounts deleted before the rule) — name it and don't
            // offer a dead link into a gone account (ux X3).
            <span className="inline-flex items-center gap-1.5">
              {client.name}
              <StatusPill tone="closed">Account deleted</StatusPill>
            </span>
          ) : (
            <Link to={`/clients/${client.id}`} className="hover:text-ink">
              {client.name}
            </Link>
          )}
        </div>
        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <ProjectNameTitle
            projectId={project.id}
            name={project.name}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: [tabsKey, id, "detail"] })}
          />
          {/* The mid-flow stage pill is redundant with the workspace sub-nav
              below (the active tab already names the current stage) — dropped.
              Only the state pill remains, and only when it's meaningful (on_hold
              / closed) rather than the default "open" mechanical noise (UI 11). */}
          {isMeaningfulProjectState(project.statusState) && (
            <StatusPill tone="dormant">
              {projectStateLabel(project.statusState)}
            </StatusPill>
          )}
          {/* Terminal outcome — statusStage closed/lost is a SEPARATE enum from
              statusState ("Mark Lost" sets statusStage, never statusState), so
              the state pill above can't surface it. Restores the deal-outcome
              signal the removed stage rail's terminal end-cap used to carry —
              the only workspace surface that showed a lost/closed deal. */}
          {/* Legacy closed/lost pill — only before opportunity stages are on;
              after that the stage bar below owns Lost / Reopen. */}
          {!data.sales && (project.statusStage === "closed" ||
            project.statusStage === "lost") && (
            <span className="inline-flex items-center gap-1.5">
              <StatusPill tone="closed">
                {projectStageLabel(project.statusStage)}
              </StatusPill>
              {/* Reopen — quiet, secondary affordance beside the terminal pill,
                  and the ONLY project-header action (the Actions ▾ dropdown that
                  duplicated the tab action rows was removed 2026-07-27). Never a
                  primary (peach) button: reopening a closed/lost deal is a rare
                  correction, not the header's headline action. Direct mutation to
                  the derived destination stage (P3). */}
              <Button
                type="button"
                variant="ghost"
                onClick={() => reopenProject.mutate(reopenTargetStage)}
                disabled={reopenProject.isPending}
                className="h-7 gap-1 px-2 text-xs font-semibold text-brown-400"
                title={`Reopen this project — returns it to the pipeline at ${projectStageLabel(
                  reopenTargetStage,
                )}.`}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                {reopenProject.isPending ? "Reopening…" : "Reopen"}
              </Button>
            </span>
          )}
        </div>
        <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
          <HeaderFact label="Opportunity">#{project.projectNumber}</HeaderFact>
          {/* Work progress (presentation → quote → production → shipped →
              invoiced). The SALES stage (Open / Awaiting / Won / Lost) is the
              bar below — the two are tracked separately (CEO 2026-10-15). */}
          <HeaderFact label="Progress">{projectStageLabel(project.statusStage)}</HeaderFact>
          <HeaderFact label="In hands">{formatInHands(project.inHandsDate)}</HeaderFact>
          {project.budgetAmount && (
            <HeaderFact label="Budget">
              {Number(project.budgetAmount).toLocaleString(undefined, {
                style: "currency",
                currency: project.budgetCurrency ?? "USD",
              })}
            </HeaderFact>
          )}
          {data.clientRep && <HeaderFact label="Rep">{data.clientRep.fullName}</HeaderFact>}
          {project.propertyName && <HeaderFact label="Property">{project.propertyName}</HeaderFact>}
        </dl>
        </div>

        {/* Admin-only delete — the project workspace's one destructive action.
            Soft delete (recoverable by an operator); non-admins never see it.
            Home chosen 2026-07-29: the header's right cluster, mirroring the
            Reopen affordance's quiet-destructive grammar. Placed here (not on a
            document detail page owned by a parallel branch) to avoid collision. */}
        <div className="flex shrink-0 items-center">
          <DeleteEntityAction
            deletePath={`/projects/${project.id}`}
            triggerLabel="Delete opportunity"
            confirmTitle="Delete this project?"
            confirmBody={
              <>
                <strong className="font-semibold text-brown-500">
                  {project.name}
                </strong>{" "}
                {buildProjectDeleteBody({
                  hasCommittedSalesOrder: stageCount("sales_order") > 0,
                  purchaseOrderCount: stageCount("production"),
                  invoiceCount: stageCount("invoice"),
                })}
              </>
            }
            redirectTo="/projects"
            flashMessage={`Project “${project.name}” deleted.`}
            errorFallback="Couldn't delete this project. Refresh and try again."
            // Include ["clients"] so the account page's block count (which gates
            // its own delete) refreshes immediately on return — prefix-matches
            // ["clients", id, "detail"], beating the 30s global staleTime. Without
            // it the "delete projects → account becomes deletable" flow could show
            // a stale activeProjectCount for up to 30s (ux 2026-07-29).
            invalidateKeys={[["projects"], ["clients"], ["sales", "pipeline"]]}
            // Honest gate (ux SHOULD-FIX): warn about live public links on FIRST
            // open, not after a refused submit. Runs the SAME shared decision the
            // server enforces, over the counts already in the detail payload.
            preWarning={projectSentDocsRefusal(data.sentDocs)?.detail ?? null}
            // Round-53 follow-up (chip task_2575ab52): the server refuses (409)
            // when the project has documents already SENT to vendors/the client —
            // their public share links stay live after a soft delete. An admin can
            // override; the override is audited server-side. The 409 detail is
            // surfaced verbatim (extractDeleteErrorMessage); this wires the honest
            // "Delete anyway" affordance + consequence copy.
            override={{
              flag: "override_sent_documents",
              errorCode: PROJECT_HAS_SENT_DOCUMENTS,
              actionLabel: "Delete anyway",
              consequenceNote: PROJECT_SENT_DOCS_OVERRIDE_NOTE,
            }}
          />
        </div>
      </div>

      {data.sales && (
        <OpportunitySalesBar
          projectId={project.id}
          sales={data.sales}
          reviewHref={`/projects/${project.id}/quotes`}
          invalidateKeys={[[tabsKey, id, "detail"], ["projects"]]}
        />
      )}

      {/* Reopen confirmation (P5) — same cream/brown success grammar the Sales
          Pipeline board uses for its stage-move banner, so the two deal-stage
          surfaces read consistently. */}
      {reopenNotice && (
        <div className="mb-5 rounded-md bg-ok-bg px-4 py-3 text-sm text-ok" role="status">
          {reopenNotice}
        </div>
      )}

      {/* Body: project side nav (grouped by what happens when) + tab content.
          Below lg the nav becomes one sideways-scrolling tab row so the
          content keeps the full phone width. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[184px_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav
            aria-label="Opportunity sections"
            className="-mx-4 flex gap-x-5 overflow-x-auto overflow-y-hidden border-b border-rule px-4 lg:mx-0 lg:flex-col lg:gap-0 lg:overflow-visible lg:border-0 lg:px-0"
          >
            {tabs.map((t, i) => (
              <div key={t.to} className="contents">
                {t.group && (
                  <p
                    className={cn(
                      "hidden px-3 pb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted lg:block",
                      i === 0 ? "pt-0" : "pt-4",
                    )}
                  >
                    {t.group}
                  </p>
                )}
                <NavLink
                  to={t.to}
                  className={({ isActive }) =>
                    cn(
                      "-mb-px flex min-h-[44px] shrink-0 items-center gap-2.5 whitespace-nowrap border-b-2 text-sm transition-colors lg:mb-0 lg:min-h-[38px] lg:rounded-md lg:border-b-0 lg:px-3",
                      isActive
                        ? "border-ink font-medium text-ink lg:bg-white lg:shadow-[inset_2px_0_0_#080808]"
                        : // Empty tabs (no content yet — Presentation / Quote)
                          // stay clearly NAVIGABLE: a lighter-but-interactive
                          // shade with the SAME hover as a populated tab, NOT
                          // opacity-50 — the disabled read a first real client hit
                          // (CEO 2026-07-29). The count (only when > 0) carries
                          // has-content vs empty.
                          t.ready
                          ? "border-transparent text-ink/80 hover:text-ink lg:hover:bg-white/70"
                          : "border-transparent text-muted hover:text-ink lg:hover:bg-white/70",
                    )
                  }
                >
                  <t.icon className="hidden h-4 w-4 shrink-0 text-muted lg:block" aria-hidden />
                  <span className="lg:flex-1">{t.label}</span>
                  {t.count !== undefined && t.count > 0 && (
                    <span className="text-xs tabular-nums text-muted">
                      {t.count}
                    </span>
                  )}
                </NavLink>
              </div>
            ))}
          </nav>
        </aside>

        {/* Tab content */}
        <div className="min-w-0">
          <Outlet context={data satisfies ProjectDetailResponse} />
        </div>
      </div>
    </div>
  );
}

function HeaderFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">{children}</dd>
    </div>
  );
}

function formatInHands(s: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || "Not set";
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Lightweight tab-stub component used by sub-tabs that don't have content
// yet (Files, Estimate, POs, Invoice, Bills, Commissions, Feedback). Mirrors
// the commonsku "greyed sub-tab" convention
// (`docs/commonsku-audit/commonsku-v2-feature-diff.md` line 80).
export function ProjectTabStub({
  title,
  note,
}: {
  title: string;
  note?: string;
}) {
  return (
    <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
      <p className="text-xs text-muted">{title}</p>
      <h2 className="mt-2 text-lg font-semibold text-ink">On the way.</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        {note ??
          "Backend is wired; the UI for this sub-tab ships in a follow-up PR of the internal-launch sprint."}
      </p>
    </div>
  );
}

export type { ProjectDetailResponse };

/** The opportunity's name — click it to rename (Stephanie 2026-10-27: "how
 *  to change the name of an opportunity"). Saves on Enter or when you click
 *  away; Escape puts it back. */
function ProjectNameTitle(props: { projectId: string; name: string; onSaved: () => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (name: string) =>
      api(`/projects/${props.projectId}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      props.onSaved();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Couldn't rename it — try again."),
  });
  const commit = () => {
    if (draft === null) return;
    const next = draft.trim();
    if (next === "" || next === props.name) {
      setDraft(null);
      setError(null);
      return;
    }
    save.mutate(next);
  };
  const titleCls = "min-w-0 break-words text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink";
  if (draft === null) {
    return (
      <h1 className={titleCls}>
        <button
          type="button"
          onClick={() => setDraft(props.name)}
          title="Click to rename"
          className="group inline-flex items-center gap-2 text-left hover:text-brown-400"
        >
          {props.name}
          <Pencil className="h-4 w-4 text-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
          <span className="sr-only">Rename</span>
        </button>
      </h1>
    );
  }
  return (
    <div className="min-w-0 flex-1">
      <input
        autoFocus
        value={draft}
        maxLength={200}
        disabled={save.isPending}
        aria-label="Opportunity name"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(null);
            setError(null);
          }
        }}
        className={cn(titleCls, "w-full max-w-3xl rounded-md border border-brown-200 bg-white px-2 py-0.5 focus:border-peach-500 focus:outline-none")}
      />
      {error && <p className="mt-1 text-xs text-rust-600">{error}</p>}
    </div>
  );
}
